import { Link } from 'react-router-dom';
import { ArrowLeft, Heart } from 'lucide-react';

export default function Terms() {
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8 group"
        >
          <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
          Back
        </Link>

        <div className="flex items-center gap-2 mb-8">
          <Heart className="h-6 w-6 text-primary" />
          <span className="text-lg font-bold">Apne<span className="text-primary">Doctors</span></span>
        </div>

        <h1 className="text-3xl font-bold text-foreground mb-2">Terms of Service</h1>
        <p className="text-sm text-muted-foreground mb-8">Last updated: February 2026</p>

        <div className="prose prose-sm dark:prose-invert max-w-none space-y-6 text-muted-foreground">

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-2">1. Acceptance of Terms</h2>
            <p>
              By accessing or using ApneDoctors ("the Platform"), you agree to be bound by these Terms of
              Service. If you do not agree, do not use the Platform.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-2">2. Nature of the Service</h2>
            <p>
              ApneDoctors is a telemedicine facilitation platform. It enables patients to connect with
              licensed medical professionals via video consultation and provides AI-powered tools that
              support (but do not replace) clinical decision-making.
            </p>
            <p className="mt-2 font-medium text-foreground">
              AI-generated outputs are informational only. All clinical decisions must be made by a
              qualified, licensed medical professional.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-2">3. Medical Disclaimer</h2>
            <p>
              The Platform does not provide medical diagnoses. Information provided through AI tools is
              intended to support — not replace — professional medical advice. Always seek the advice of
              a qualified health provider with questions regarding a medical condition.
            </p>
            <p className="mt-2">
              <strong>In a medical emergency, call 112 (India) or visit the nearest emergency room immediately.</strong>
              Do not use this platform in life-threatening situations.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-2">4. User Eligibility</h2>
            <ul className="list-disc list-inside space-y-1">
              <li>You must be at least 18 years old, or have guardian consent, to use the Platform.</li>
              <li>Doctors must hold a valid, current medical licence issued by a recognised authority.</li>
              <li>You must provide accurate information when creating an account.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-2">5. Doctor Verification</h2>
            <p>
              All doctors are subject to credential verification before access to clinical features is
              granted. ApneDoctors reserves the right to suspend or revoke access if credentials are
              found to be invalid, expired, or misrepresented.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-2">6. Privacy & Data</h2>
            <p>
              Your health data is processed in accordance with our{' '}
              <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>.
              Health records are stored securely and accessible only to you, treating doctors, and
              authorised platform administrators.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-2">7. Prohibited Use</h2>
            <ul className="list-disc list-inside space-y-1">
              <li>Impersonating a licensed medical professional.</li>
              <li>Misusing AI tools to make unsupported medical claims.</li>
              <li>Sharing another user's health data without consent.</li>
              <li>Attempting to circumvent platform security controls.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-2">8. Limitation of Liability</h2>
            <p>
              ApneDoctors and its operators are not liable for clinical decisions made by doctors, patient
              outcomes, or misuse of AI-generated information. The Platform facilitates connections
              between patients and independent licensed professionals.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-2">9. Changes to Terms</h2>
            <p>
              We may update these Terms. Continued use of the Platform after changes constitutes
              acceptance of the revised Terms. Material changes will be communicated via email.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-2">10. Governing Law</h2>
            <p>
              These Terms are governed by the laws of India. Disputes shall be subject to the exclusive
              jurisdiction of courts in Mumbai, Maharashtra.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-foreground mb-2">Contact</h2>
            <p>
              For questions about these Terms, contact us via the platform's contact form or at{' '}
              <span className="text-foreground">legal@apnedoctors.in</span>.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
